import { useCallback, useContext, useEffect, useRef, useState } from "react";
import "./styles.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import StepCompetences from "../StepCompetences";
import StepRealisations from "../StepRealisations";
import StepDocuments from "../StepDocuments";
import StepCertifications from "../StepCertifications";
import EmailCodeVerification from "../EmailCodeVerification";
import { useNavigate } from "react-router-dom";
import { GoogleMapsContext } from "../../context/GoogleMapsProvider";
import GoogleAddressSearch from "../GoogleAddressSearch";
import GoogleMapView from "../GoogleMapView";
import Cgu from "../Cgu";

export default function InscriptionPrestataire({ onClose, onSwitch }) {
    const apiUrl = process.env.REACT_APP_API_URL;
    const photoInputRef = useRef(null);
    const logoInputRef = useRef(null);
    const { isLoaded } = useContext(GoogleMapsContext);

    const [loading, setLoading] = useState(false);
    const [openService, setOpenService] = useState(null);
    const [secteurs, setSecteurs] = useState([]);
    const [selectedSecteur, setSelectedSecteur] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [inscriptionSuccess, setInscriptionSuccess] = useState(false);
    const [cguAccepted, setCguAccepted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    const refreshSecteurs = useCallback(() => {
        setLoading(true);
        fetch(`${apiUrl}/api/secteurs`)
            .then((res) => res.json())
            .then((data) => setSecteurs(data))
            .catch((err) => console.error(err))
            .finally(() => setLoading(false));
    }, [apiUrl]);

    useEffect(() => {
        refreshSecteurs();
    }, [refreshSecteurs]);

    function computeFrenchTVAFromSiret(siret) {
        if (!siret || siret.length < 9) return "";

        const siren = siret.substring(0, 9);
        if (!/^\d{9}$/.test(siren)) return "";

        const key = (12 + 3 * (parseInt(siren, 10) % 97)) % 97;
        const formattedKey = key.toString().padStart(2, "0");

        return `FR${formattedKey}${siren}`;
    }
    const [loadingSiret, setLoadingSiret] = useState(false);
    const [errorSiret, setErrorSiret] = useState("");

    const [formData, setFormData] = useState({
        email: "",
        password: "",
        passwordRepeat: "",
        token: "",
        nom: "",
        prenom: "",
        dateOfBirthRepresentant: "",
        phoneNumber: "",
        siteWeb: "",
        photoProfil: null,
        logoEntreprise: null,
        affichagePref: "photo",
        typeCompany: "",
        secteurCompany: "",
        companyAddress: "",
        companyPostalCode: "",
        companyCity: "",
        companyCountry: "fr",
        companyLat: null,
        companyLng: null,
        travailDistance: false,
        emergencyPossible: false,
        isCesuAccepted: false,
        rayonIntervention: "",
        raisonSociale: "",
        nonAssujettiTva: false,
        siret: "",
        numTva: "",
        presentationText: "",
        services: [],
        competences: [],
        certifications: [],
        realisations: [],
        pieceIdentite: null,
        justificatifEntreprise: null,
        autorisationSpecifique: false,
        docAutorisation: null,
    });
    const handlePlaceSelect = (place) => {
        // Extraction des composants d'adresse
        const components = place.address_components || [];
        let streetNumber = "", route = "", postal = "", city = "";

        components.forEach((c) => {
            if (c.types.includes("street_number")) streetNumber = c.long_name;
            if (c.types.includes("route")) route = c.long_name;
            if (c.types.includes("postal_code")) postal = c.long_name;
            if (c.types.includes("locality")) city = c.long_name;
            if (!city && c.types.includes("administrative_area_level_2")) city = c.long_name;
        });

        const formattedAddress = [streetNumber, route].filter(Boolean).join(" ") + ", " + postal + " " + city;

        // Fonction pour mettre à jour le formData
        const updateFormData = (lat, lng) => {
            setFormData((prev) => ({
                ...prev,
                companyAddress: [streetNumber, route].filter(Boolean).join(" "),
                companyPostalCode: postal,
                companyCity: city,
                companyLat: lat,
                companyLng: lng,
            }));

            validateField("companyAddress", [streetNumber, route].filter(Boolean).join(" "));
            validateField("companyPostalCode", postal);
            validateField("companyCity", city);
        };

        // Vérifie si place.geometry existe
        if (place.geometry && place.geometry.location) {
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            updateFormData(lat, lng);
        } else {
            // Sinon, utiliser Geocoder pour obtenir lat/lng à partir de l'adresse
            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ address: formattedAddress }, (results, status) => {
                if (status === "OK" && results[0]) {
                    const lat = results[0].geometry.location.lat();
                    const lng = results[0].geometry.location.lng();
                    updateFormData(lat, lng);
                } else {
                    console.error("Impossible de récupérer les coordonnées :", status, place);
                    updateFormData(null, null); // vide lat/lng si échec
                }
            });
        }
    };


    const [step, setStep] = useState(1);
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordRepeat, setShowPasswordRepeat] = useState(false);
    const [errors, setErrors] = useState({});
    const handleNext = () => setStep(step + 1);
    const handlePrev = () => setStep(step - 1);
    // Vérifie que le SIRET fait 14 chiffres et respecte la clé de Luhn
    const isValidSiret = (siret) => {
        if (!/^\d{14}$/.test(siret)) return false;
        let sum = 0;
        for (let i = 0; i < 14; i++) {
            let num = parseInt(siret[i]);
            if (i % 2 === 0) num *= 2;
            if (num > 9) num -= 9;
            sum += num;
        }
        return sum % 10 === 0;
    };

    // Vérifie le format du numéro de TVA intracommunautaire FR + clé + SIREN
    const isValidTva = (tva) => /^FR\d{2}\d{9}$/.test(tva.replace(/\s+/g, ''));
    const handleGetInfosPresta = async () => {
        setErrorSiret("");

        // Vérification SIRET
        if (!formData.siret || !isValidSiret(formData.siret)) {
            setErrorSiret("SIRET invalide");
            return;
        }
        try {
            setLoadingSiret(true);

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/apiSirene?siret=${formData.siret}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json"
                    }
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Entreprise introuvable");
            }

            // Préremplissage du formulaire
            setFormData(prev => ({
                ...prev,
                raisonSociale: data.raisonSociale || prev.raisonSociale,
                typeCompany: data.statutPrestataire || prev.typeCompany,
                secteurCompany: data.activitePrincipale || prev.secteurCompany,
                companyAddress: data.adresse || prev.companyAddress,
                companyPostalCode: data.codePostal || prev.companyPostalCode,
                companyCity: data.ville || prev.companyCity,
                companyLat: data.latitude || prev.companyLat,
                companyLng: data.longitude || prev.companyLng,
                nom: data.lastNamePrestataire || prev.nom,
                prenom: data.firstNamePrestataire || prev.prenom,


            }));
            setStep(2);

        } catch (error) {
            setErrorSiret(error.message);
        } finally {
            setLoadingSiret(false);
        }


    }


    const passwordRegex =
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{8,}$/;
    useEffect(() => {
        console.log("Adresse mise à jour :", formData.companyAddress, formData.companyPostalCode, formData.companyCity);
        console.log("lat " + formData.companyLat + "lng " + formData.companyLng);
    }, [formData.companyAddress, formData.companyPostalCode, formData.companyCity, formData.companyLat, formData.companyLng]);

    useEffect(() => {
        const { siret, typeCompany, nonAssujettiTva, numTva } = formData;

        const isAssujettiTVA =
            typeCompany !== "micro-entrepreneur" ||
            (typeCompany === "micro-entrepreneur" && !nonAssujettiTva);

        if (!isAssujettiTVA) {
            return;
        }

        if (!siret || siret.replace(/\s/g, "").length !== 14) {
            return;
        }

        const computedTVA = computeFrenchTVAFromSiret(
            siret.replace(/\s/g, "")
        );

        // Ne pas écraser une correction manuelle
        if (computedTVA && numTva !== computedTVA) {
            setFormData((prev) => ({
                ...prev,
                numTva: computedTVA
            }));
        }
    }, [
        formData.siret,
        formData.typeCompany,
        formData.nonAssujettiTva
    ]);

    // --- Validation des champs ---
    const validateField = (name, value) => {
        let fieldErrors = { ...errors };

        switch (name) {
            case "email":
                if (!value) fieldErrors.email = "L'email est requis";
                else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
                    fieldErrors.email = "L'adresse email est invalide.";
                else {
                    delete fieldErrors.email;
                    fetch(`${apiUrl}/api/register/checkMail?email=${encodeURIComponent(value)}`)
                        .then((res) => res.json())
                        .then((data) => {
                            if (data.exists) {
                                // Si ton API renvoie { exists: true }
                                setErrors((prev) => ({
                                    ...prev,
                                    email: "Cet email est déjà utilisé."
                                }));
                            } else {
                                setErrors((prev) => {
                                    const { email, ...rest } = prev;
                                    return rest; // supprime l'erreur email
                                });
                            }
                        })
                        .catch((err) => {
                            console.error("Erreur vérification email:", err);
                        });
                }
                break;

            case "password":
                if (!value) fieldErrors.password = "Le mot de passe est requis";
                else if (!passwordRegex.test(value))
                    fieldErrors.password = (
                        <ul className="password-rules">
                            <li className={value.length >= 8 ? "ok" : "error"}>
                                {value.length >= 8 ? "✅" : "❌"} 8 caractères minimum
                            </li>
                            <li className={/[A-Z]/.test(value) ? "ok" : "error"}>
                                {/[A-Z]/.test(value) ? "✅" : "❌"} 1 majuscule
                            </li>
                            <li className={/[a-z]/.test(value) ? "ok" : "error"}>
                                {/[a-z]/.test(value) ? "✅" : "❌"} 1 minuscule
                            </li>
                            <li className={/\d/.test(value) ? "ok" : "error"}>
                                {/\d/.test(value) ? "✅" : "❌"} 1 chiffre
                            </li>
                            <li
                                className={
                                    /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value)
                                        ? "ok"
                                        : "error"
                                }
                            >
                                {/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value)
                                    ? "✅"
                                    : "❌"}{" "}
                                1 caractère spécial
                            </li>
                        </ul>
                    );
                else delete fieldErrors.password;
                break;

            case "passwordRepeat":
                if (formData.password !== value)
                    fieldErrors.passwordRepeat = "Les mots de passes ne sont pas identiques";
                else delete fieldErrors.passwordRepeat;
                break;

            case "nom":
                if (!value || value.length < 2) fieldErrors.nom = "Le nom doit contenir au moins 2 caractères";
                else delete fieldErrors.nom;
                break;

            case "prenom":
                if (!value || value.length < 2) fieldErrors.prenom = "Le prénom doit contenir au moins 2 caractères";
                else delete fieldErrors.prenom;
                break;

            case "dateOfBirthRepresentant":
                if (!value) fieldErrors.dateOfBirthRepresentant = "Le champ Date de Naissance est requis";
                else {
                    const birthDate = new Date(value);
                    const today = new Date();
                    let age = today.getFullYear() - birthDate.getFullYear();
                    const m = today.getMonth() - birthDate.getMonth();
                    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
                    if (age < 18) fieldErrors.dateOfBirthRepresentant = "Vous devez avoir au moins 18 ans.";
                    else delete fieldErrors.dateOfBirthRepresentant;
                }
                break;

            case "phoneNumber":
                if (!/^\d{10}$/.test(value)) fieldErrors.phoneNumber = "Le numéro doit contenir exactement 10 chiffres";
                else delete fieldErrors.phoneNumber;
                break;
            case "raisonSociale":
                if (!value || value.length < 2) fieldErrors.raisonSociale = "La raison sociale doit contenir au moins 2 caractères";
                else delete fieldErrors.raisonSociale;
                break;
            case "typeCompany":
                if (!value) fieldErrors.typeCompany = "Le statut de l'entreprise est requis";
                else delete fieldErrors.typeCompany;
            case "secteurCompany":
                if(value === "") fieldErrors.secteurCompany = "le secteur principal est requis";
                else delete fieldErrors.secteurCompany;
                break;
            case "siret":
                if (value && !isValidSiret(value)) fieldErrors.siret = "SIRET invalide";
                else delete fieldErrors.siret;
                break;

            case "numTva":
                if (
                    formData.typeCompany === "micro-entrepreneur" &&
                    formData.nonAssujettiTva
                ) {
                    delete fieldErrors.numTva; // non requis
                } else if (value && !isValidTva(value)) {
                    fieldErrors.numTva = "Numéro de TVA invalide";
                } else {
                    delete fieldErrors.numTva;
                }
                break;
            case "companyAddress":
                if (!value || value.length < 5)
                    fieldErrors.companyAddress = "L'adresse est obligatoire";
                else delete fieldErrors.companyAddress;
                break;
            case "companyPostalCode":
                if (!value) {
                    fieldErrors.companyPostalCode = "Le code postal est obligatoire";
                } else if (!/^(?:0[1-9]|[1-8]\d|9[0-5])\d{3}$/.test(value)) {
                    fieldErrors.companyPostalCode = "Le code postal doit être un code français valide (5 chiffres).";
                } else {
                    delete fieldErrors.companyPostalCode;
                }
                break;
            case "companyCity":
                if (!value || value.length < 2) {
                    fieldErrors.companyCity = "La ville est obligatoire (2 caractères minimum)";
                } else if (!/^[a-zA-ZÀ-ÖØ-öø-ÿ\s'-]+$/.test(value)) {
                    fieldErrors.companyCity =
                        "La ville ne doit contenir que des lettres, espaces ou tirets.";
                } else {
                    delete fieldErrors.companyCity;
                }
                break;

            case "rayonIntervention":
                if (!formData.travailDistance && !value) {
                    fieldErrors.rayonIntervention =
                        "Veuillez sélectionner un rayon d'intervention";
                } else {
                    delete fieldErrors.rayonIntervention;
                }
                break;
            case "presentationText":
                if (!value || value.trim().length < 10) {
                    fieldErrors.presentationText = "Votre présentation doit contenir au moins 10 caractères.";
                } else {
                    delete fieldErrors.presentationText;
                }
                break;
            default:
                break;
        }



        setErrors(fieldErrors);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        let newValue = value;
        if (type === "checkbox") {
            newValue = checked;
        } else if (name === "rayonIntervention") {
            newValue = parseInt(value, 10); // convertit "5" => 5, "-1" => -1
        }
        if (name === "secteurCompany") {
            if (value !== "") {
                const secteur = secteurs.find(s => s.idSecteur === parseInt(value, 10));
                if (secteur) {
                    setSelectedSecteur(secteur);
                }
            } else {
                setSelectedSecteur(null); // si aucun choix
            }
        }

        setFormData((prev) => ({ ...prev, [name]: newValue }));
        validateField(name, newValue);
    };


    // --- Gestion fichiers ---
    const handleFileChange = (e, type) => {
        const file = e.target.files[0];
        if (!file) return;
        if (type === "photoProfil") {
            setFormData(prev => ({ ...prev, photoProfil: file }));
            setPhotoPreview(URL.createObjectURL(file));
        } else if (type === "logoEntreprise") {
            setFormData(prev => ({ ...prev, logoEntreprise: file }));
            setLogoPreview(URL.createObjectURL(file));
        }
        // Supprimer l'erreur files si maintenant valide
        setErrors(prev => {
            const { files, ...rest } = prev;
            return rest;
        });
    };

    const handleRemoveFile = (type) => {
        if (type === "photoProfil") {
            if (photoPreview) URL.revokeObjectURL(photoPreview);
            setPhotoPreview(null);
            if (photoInputRef.current) photoInputRef.current.value = "";
            setFormData(prev => {
                const newData = { ...prev, photoProfil: null };

                return newData;
            });
        } else if (type === "logoEntreprise") {
            if (logoPreview) URL.revokeObjectURL(logoPreview);
            setLogoPreview(null);
            if (logoInputRef.current) logoInputRef.current.value = "";
            setFormData(prev => {
                const newData = { ...prev, logoEntreprise: null };

                return newData;
            });
        }
    };


    const handleSubmit = async () => {
        //e.preventDefault();
        if (isSubmitting) return;
        setIsSubmitting(true);
        if (Object.keys(errors).length > 0) {
            alert("Veuillez corriger les erreurs avant de soumettre.");
            return;
        }

        try {

            const form = new FormData();

            // Champs principaux
            Object.keys(formData).forEach(key => {
                if (formData[key] && typeof formData[key] !== "object") {
                    form.append(key, formData[key]);
                }
            });

            // Fichiers principaux
            if (formData.photoProfil) form.append("photoProfil", formData.photoProfil);
            if (formData.logoEntreprise) form.append("logoEntreprise", formData.logoEntreprise);

            // ✅ Compétences (anciennement intervenants)
            if (formData.competences && Array.isArray(formData.competences)) {
                formData.competences.forEach((comp, index) => {
                    form.append(`competences[${index}]`, comp.idMetier);

                });
            }
            if (formData.certifications && Array.isArray(formData.certifications)) {
                formData.certifications.forEach((certif, index) => {
                    form.append(`certifications[${index}]`, certif);
                });
            }


            const response = await fetch(`${apiUrl}/api/register/prestataire`, { method: "POST", body: form });

            if (!response.ok) {
                const errorData = await response.json();
                alert(errorData.message || "Une erreur est survenue lors de l’inscription.");
                return;
            }

            const data = await response.json();
            setInscriptionSuccess(true);

        } catch (error) {
            console.error("Erreur lors de l’inscription :", error);
            alert("Impossible de se connecter au serveur. Réessayez plus tard.");
        }
        setIsSubmitting(false);
    };

    // --- Validité des étapes ---
    const isStep1Valid = !errors.email && !errors.password && !errors.passwordRepeat && formData.email && formData.password && formData.passwordRepeat;
    const isStep2Valid = !errors.nom && !errors.prenom && !errors.dateOfBirthRepresentant && !errors.phoneNumber && formData.nom && formData.prenom && formData.dateOfBirthRepresentant && formData.phoneNumber;
    const isStep4Valid =
        // Présence des champs obligatoires
        !!formData.raisonSociale &&
        !!formData.typeCompany &&
        !!formData.siret &&
        !!formData.secteurCompany &&

        // Pas d’erreurs
        !errors.raisonSociale &&
        !errors.typeCompany &&
        !errors.siret &&
        !errors.secteurCompany &&

        (
            // TVA pas obligatoire si micro-entrepreneur non assujetti
            (formData.typeCompany === "micro-entrepreneur" && formData.nonAssujettiTva) ||
            (
                !!formData.numTva &&
                !errors.numTva
            )
        );

    const isStep5Valid =
        !errors.companyAddress && !errors.companyPostalCode && !errors.companyCity && formData.companyAddress && formData.companyPostalCode && formData.companyCity &&
        (
            // Si travail à distance → pas besoin de rayon
            formData.travailDistance ||
            (!!formData.rayonIntervention && !errors.rayonIntervention)
        );

    const isStep6Valid = !!formData.presentationText && formData.presentationText.trim().length >= 10;


    return (
        <div className="inscriptionPrestataireModalOverlay">
            <div className="inscriptionPrestataireModal">
                <button className="inscriptionPrestataireModalClose" onClick={onClose}>×</button>

                {!inscriptionSuccess && (<><h2>Inscription Prestataire</h2>
                    {step === 0 && (
                        <div>
                            <h3>Bienvenue sur INTERESEAUX</h3>
                            <p>
                                <strong style={{ color: "red" }}>Vous êtes ici pour créer un compte prestataire</strong> : cet espace est réservé aux
                                professionnels qui souhaitent trouver des clients, répondre à des projets et développer leur activité.
                            </p>

                            <p style={{ marginTop: "15px" }}>
                                <strong>Vous n’êtes PAS au bon endroit si :</strong>
                            </p>

                            <ul style={{ marginTop: "10px" }}>
                                <li>
                                    ⚠️ <strong>Vous êtes client</strong> et cherchez des prestataires pour réaliser vos projets.
                                    Dans ce cas, rendez-vous dans l’espace client en cliquant
                                    <strong onClick={() => navigate("/")} style={{ cursor: "pointer" }}> ici</strong>.
                                </li>
                                <li style={{ marginTop: "10px" }}>
                                    ⚠️ <strong>Vous êtes donneur d’ordres (grande entreprise)</strong> et souhaitez publier des
                                    appels d’offres importants.
                                    Accédez à l’espace dédié en cliquant
                                    <strong onClick={() => navigate("/espace-donneur-ordre")} style={{ cursor: "pointer" }}> ici</strong>.
                                </li>
                            </ul>

                            <button className="btn-primary" onClick={() => setStep(1)} style={{ marginTop: "15px" }}>
                                CONTINUER
                            </button>
                        </div>
                    )}
                    <form autoComplete="off" onSubmit={handleSubmit}>
                        {/* Étape 1 */}
                        {step === 1 && (
                            <>
                                <h3>Vos identifiants de connexion</h3>

                                <div className="inscriptionPrestataireModal-input-form">
                                    <label>Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        autoComplete="new-item"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="Entrez votre email"
                                        required
                                    />
                                    {errors.email && <p className="error-text">{errors.email}</p>}
                                </div>

                                <div className="inscriptionPrestataireModal-input-form">
                                    <label htmlFor="password">Mot de passe</label>
                                    <div className="password-wrapper">
                                        <input
                                            id="password"
                                            name="password"
                                            type={showPassword ? "text" : "password"}
                                            autoComplete="new-password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            placeholder="Votre mot de passe"
                                            required
                                        />
                                        <span className="togglePassword" onClick={() => setShowPassword(v => !v)}>
                                            <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                        </span>
                                    </div>
                                    {errors.password && <>{errors.password}</>}
                                </div>

                                <div className="inscriptionPrestataireModal-input-form">
                                    <label htmlFor="passwordRepeat">Répétez le mot de passe</label>
                                    <div className="password-wrapper">
                                        <input
                                            id="passwordRepeat"
                                            name="passwordRepeat"
                                            type={showPasswordRepeat ? "text" : "password"}
                                            autoComplete="new-password"
                                            value={formData.passwordRepeat}
                                            onChange={handleChange}
                                            placeholder="Répétez votre mot de passe"
                                            required
                                        />
                                        <span className="togglePassword" onClick={() => setShowPasswordRepeat(v => !v)}>
                                            <FontAwesomeIcon icon={showPasswordRepeat ? faEyeSlash : faEye} />
                                        </span>
                                    </div>
                                    {errors.passwordRepeat && <p className="error-text">{errors.passwordRepeat}</p>}
                                </div>

                                <div className="inscriptionPrestataireModal-input-form">
                                    <button
                                        type="button"
                                        onClick={() => setStep(12)}
                                        disabled={!isStep1Valid}
                                    >
                                        Suivant
                                    </button>
                                </div>

                                <p className="modal-switch">
                                    Déjà inscrit ? <span onClick={onSwitch}>Connectez-vous</span>
                                </p>
                            </>
                        )}
                        {step === 12 && (
                            <EmailCodeVerification
                                formData={formData}
                                onVerified={(token) => {
                                    setFormData(prev => ({
                                        ...prev,
                                        token: token
                                    }));
                                    setTimeout(() => console.log("State réel :", token), 500);
                                    setStep(13);
                                }}
                            />
                        )}
                        {
                            step === 13 && (
                                <>

                                    <h3>Votre numéro de SIRET</h3>
                                    <div className="inscriptionPrestataireModal-input-form">

                                        <input type="text" name="siret" value={formData.siret} onChange={handleChange} required placeholder="SIRET"

                                        />
                                        {errorSiret && <p style={{ color: "red" }}>{errorSiret}</p>}
                                    </div>
                                    <button
                                        type="button"
                                        className="btn-primary"
                                        onClick={handleGetInfosPresta}
                                        disabled={loadingSiret}
                                    >
                                        {loadingSiret ? "Recherche..." : "Récupérer les informations"}
                                    </button>


                                    <div className="inscriptionClientModal-actions">
                                        <button type="button" onClick={() => setStep(1)}>Précédent</button>
                                    </div>
                                </>
                            )
                        }
                        {/* Étape 2 */}
                        {step === 2 && (
                            <>
                                <h3>Représentant légal</h3>
                                <div className="inscriptionPrestataireModal-input-form">
                                    <label>Nom</label>
                                    <input autoComplete="new-item" type="text" name="nom" value={formData.nom} onChange={handleChange} required />
                                    {errors.nom && <p className="error-text">{errors.nom}</p>}
                                </div>
                                <div className="inscriptionPrestataireModal-input-form">
                                    <label>Prénom</label>
                                    <input autoComplete="jkghgj" type="text" name="prenom" value={formData.prenom} onChange={handleChange} required />
                                    {errors.prenom && <p className="error-text">{errors.prenom}</p>}
                                </div>
                                <div className="inscriptionPrestataireModal-input-form">
                                    <label>Date de naissance</label>
                                    <input autoComplete="jkghgj" type="date" name="dateOfBirthRepresentant" value={formData.dateOfBirthRepresentant} onChange={handleChange}
                                        max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split("T")[0]} />
                                    {errors.dateOfBirthRepresentant && <p className="error-text">{errors.dateOfBirthRepresentant}</p>}
                                </div>
                                <div className="inscriptionPrestataireModal-input-form">
                                    <label>N° de téléphone</label>
                                    <input autoComplete="new-password" type="text" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} required />
                                    {errors.phoneNumber && <p className="error-text">{errors.phoneNumber}</p>}
                                </div>
                                <div className="inscriptionPrestataireModal-input-form">
                                    <label>Site web</label>
                                    <input type="text" name="siteWeb" value={formData.siteWeb} onChange={handleChange} />
                                </div>
                                <div className="inscriptionClientModal-actions">
                                    <button type="button" onClick={() => setStep(13)}>Précédent</button>
                                    <button type="button" disabled={!isStep2Valid} onClick={() => setStep(3)}>Suivant</button>
                                </div>
                            </>
                        )}

                        {/* Étape 3 */}
                        {step === 3 && (
                            <>
                                <h3>Photo de profil et logo (Optionnel)</h3>
                                <div className="inscriptionPrestataireModal-input-form">
                                    <label>Photo de profil</label>
                                    <input ref={photoInputRef} type="file" accept="image/*" onChange={(e) => handleFileChange(e, "photoProfil")} />
                                    {photoPreview && (
                                        <div style={{ width: "fit-content", margin: "auto" }}>
                                            <img src={photoPreview} alt="Prévisualisation" style={{ width: "120px", height: "120px", borderRadius: "50%", objectFit: "cover" }} />
                                            <button type="button" onClick={() => handleRemoveFile("photoProfil")} style={{ display: "block", margin: "auto", marginTop: "5px", background: "red", color: "white", border: "none", borderRadius: "5px", padding: "5px 10px", cursor: "pointer" }}>Supprimer</button>
                                        </div>
                                    )}
                                </div>
                                <div className="inscriptionPrestataireModal-input-form">
                                    <label>Logo de l'entreprise</label>
                                    <input ref={logoInputRef} type="file" accept="image/*" onChange={(e) => handleFileChange(e, "logoEntreprise")} />
                                    {logoPreview && (
                                        <div style={{ width: "fit-content", margin: "auto" }}>
                                            <img src={logoPreview} alt="Prévisualisation logo" style={{ width: "150px" }} />
                                            <button type="button" onClick={() => handleRemoveFile("logoEntreprise")} style={{ display: "block", margin: "auto", marginTop: "5px", background: "red", color: "white", border: "none", borderRadius: "5px", padding: "5px 10px", cursor: "pointer" }}>Supprimer</button>
                                        </div>
                                    )}
                                </div>

                                <div className="inscriptionClientModal-actions">
                                    <button type="button" onClick={() => setStep(2)}>Précédent</button>
                                    <button type="button" onClick={() => setStep(4)}>Suivant</button>
                                </div>
                            </>
                        )}

                        {/* Étape 4 : Infos entreprise */}
                        {step === 4 && (
                            <>

                                <h3>Informations entreprise</h3>
                                <div className="inscriptionPrestataireModal-input-form">
                                    <label>Raison sociale</label>
                                    <input type="text" name="raisonSociale" value={formData.raisonSociale || ""} onChange={handleChange} />
                                    {errors.raisonSociale && <p className="error-text">{errors.raisonSociale}</p>}

                                </div>
                                <div className="inscriptionPrestataireModal-input-form">
                                    <label>Secteur d'activité de l'entreprise</label>
                                    <select
                                        name="secteurCompany"
                                        value={formData.secteurCompany}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="">-- Sélectionnez un secteur d'activité --</option>
                                        {
                                            secteurs && secteurs.length > 0 && secteurs.map((secteur) => (
                                                <option value={secteur.idSecteur}>{secteur.labelSecteur}</option>
                                            ))
                                        }
                                    </select>
                                    {errors.secteurCompany && <p className="error-text">{errors.secteurCompany}</p>}
                                </div>
                                <div className="inscriptionPrestataireModal-input-form">
                                    <label>Statut de l'entreprise</label>
                                    <select
                                        name="typeCompany"
                                        value={formData.typeCompany}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="">-- Sélectionnez un statut --</option>
                                        {/* Entreprises individuelles */}
                                        <option value="micro-entrepreneur">Micro-entrepreneur (ME)</option>
                                        <option value="ei">Entreprise Individuelle (EI)</option>
                                        <option value="eirl">Entreprise Individuelle à Responsabilité Limitée (EIRL)</option>

                                        {/* Sociétés commerciales */}
                                        <option value="sarl">Société à Responsabilité Limitée (SARL)</option>
                                        <option value="eurl">Entreprise Unipersonnelle à Responsabilité Limitée (EURL)</option>
                                        <option value="sas">Société par Actions Simplifiée (SAS)</option>
                                        <option value="sasu">Société par Actions Simplifiée Unipersonnelle (SASU)</option>
                                        <option value="sa">Société Anonyme (SA)</option>
                                        <option value="snc">Société en Nom Collectif (SNC)</option>
                                        <option value="scs">Société en Commandite Simple (SCS)</option>
                                        <option value="sca">Société en Commandite par Actions (SCA)</option>

                                        {/* Autres */}
                                        <option value="autre">Autre</option>
                                    </select>
                                    {errors.typeCompany && <p className="error-text">{errors.typeCompany}</p>}

                                </div>
                                {formData.typeCompany === "micro-entrepreneur" && (
                                    <div className="inscriptionPrestataireModal-input-form">
                                        <label>
                                            <input
                                                type="checkbox"
                                                name="nonAssujettiTva"
                                                checked={formData.nonAssujettiTva || false}
                                                onChange={(e) =>
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        nonAssujettiTva: e.target.checked,
                                                        numTva: e.target.checked ? "" : prev.numTva
                                                    }))
                                                }
                                            />
                                            Non assujetti à la TVA (Article 293 B du CGI)
                                        </label>
                                    </div>
                                )}


                                <div className="inscriptionPrestataireModal-input-form">
                                    <label>N° SIRET</label>
                                    <input type="text" name="siret" value={formData.siret || ""} onChange={handleChange} />
                                    {errors.siret && <p className="error-text">{errors.siret}</p>}
                                </div>
                                {!(formData.typeCompany === "micro-entrepreneur" && formData.nonAssujettiTva) && (
                                    <div className="inscriptionClientModal-input-form">
                                        <label>N° TVA Intracommunautaire</label>
                                        <input
                                            type="text"
                                            name="numTva"
                                            value={formData.numTva || ""}
                                            onChange={handleChange}
                                        />
                                        {errors.numTva && <p className="error-text">{errors.numTva}</p>}
                                    </div>
                                )}



                                <div className="inscriptionClientModal-actions">
                                    <button type="button" onClick={() => setStep(3)}>Précédent</button>
                                    <button type="button" disabled={!isStep4Valid} onClick={() => setStep(5)}>Suivant</button>
                                </div>
                            </>
                        )}
                        {
                            step === 5 && (
                                <>
                                    <h2>Localisation de l'entreprise et rayon d'intervention</h2>
                                    <div className="inscriptionPrestataireModal-input-form">

                                        {isLoaded && !formData.companyAddress && !formData.companyPostalCode && !formData.companyCity && !formData.companyLat && !formData.companyLng && (
                                            <>
                                                <label>Rechercher une adresse</label>
                                                <GoogleAddressSearch
                                                    value=""
                                                    onChange={(inputValue) => {
                                                        // Si l'utilisateur vide le champ, on supprime les infos d'adresse
                                                        if (!inputValue) {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                companyAddress: "",
                                                                companyPostalCode: "",
                                                                companyCity: "",
                                                                companyLat: null,
                                                                companyLng: null
                                                            }));
                                                        }
                                                    }}
                                                    onSelect={handlePlaceSelect}
                                                />
                                            </>
                                        )}
                                        {formData.companyLat && formData.companyLng && (
                                            <>
                                                <div>
                                                    <h3>Adresse</h3>
                                                    <p>{formData.companyAddress}</p>
                                                    <p>{formData.companyPostalCode} {formData.companyCity}</p>
                                                    <button className="btn-primary" onClick={(e) => {
                                                        e.preventDefault();
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            companyAddress: "",
                                                            companyCity: "",
                                                            companyPostalCode: "",
                                                            companyLat: null,
                                                            companyLng: null
                                                        }));
                                                    }}>MODIFIER</button>
                                                </div>
                                                <GoogleMapView origin={{ lat: formData.companyLat, lng: formData.companyLng }} />

                                            </>
                                        )}
                                    </div>
                                    <div className="inscriptionPrestataireModal-input-form">
                                        <label>
                                            <input
                                                type="checkbox"
                                                name="travailDistance"
                                                checked={formData.travailDistance || false}
                                                onChange={handleChange}
                                            />
                                            Je propose mes services uniquement à distance
                                        </label>
                                    </div>
                                    {!formData.travailDistance && (
                                        <div className="inscriptionPrestataireModal-input-form">
                                            <label>Rayon d'intervention</label>
                                            <select
                                                name="rayonIntervention"
                                                value={formData.rayonIntervention ?? ""}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="">-- Sélectionnez un rayon --</option>
                                                <option value="5">5 km</option>
                                                <option value="10">10 km</option>
                                                <option value="20">20 km</option>
                                                <option value="50">50 km</option>
                                                <option value="100">100 km</option>
                                                <option value="200">200 km</option>
                                                <option value="1000000">Illimité</option>
                                            </select>
                                        </div>
                                    )}

                                    <div className="inscriptionClientModal-actions">
                                        <button type="button" onClick={() => setStep(4)}>Précédent</button>
                                        <button type="button" disabled={!isStep5Valid} onClick={() => setStep(6)}>Suivant</button>
                                    </div>
                                </>
                            )}

                        {
                            step === 6 && (
                                <>
                                    <h2>Présentation de l'entreprise</h2>

                                    <div className="inscriptionPrestataireModal-input-form">
                                        <label>Texte de présentation</label>
                                        <textarea
                                            name="presentationText"
                                            value={formData.presentationText || ""}
                                            onChange={handleChange}
                                            rows={6}
                                            placeholder="Décrivez vos services, votre expérience, vos points forts..."
                                            required
                                        />
                                        {errors.presentationText && (
                                            <p className="error-text">{errors.presentationText}</p>
                                        )}
                                    </div>
                                    <div className="inscriptionPrestataireModal-input-form">
                                        <label>
                                            <input
                                                type="checkbox"
                                                name="emergencyPossible"
                                                checked={formData.emergencyPossible || false}
                                                onChange={handleChange}
                                            />
                                            Je peux intervenir d'urgence 24h/24 7j/7
                                        </label>
                                    </div>
                                    <div className="inscriptionPrestataireModal-input-form">
                                        <label>
                                            <input
                                                type="checkbox"
                                                name="isCesuAccepted"
                                                checked={formData.isCesuAccepted || false}
                                                onChange={handleChange}
                                            />
                                            J'accepte les paiements CESU
                                        </label>
                                    </div>


                                    <div className="inscriptionClientModal-actions">
                                        <button type="button" onClick={() => setStep(5)}>Précédent</button>
                                        <button
                                            type="button"
                                            disabled={!isStep6Valid}
                                            onClick={() => setStep(7)}
                                        >
                                            Suivant
                                        </button>
                                    </div>
                                </>
                            )
                        }

                        {
                            step === 7 && (
                                <StepCompetences
                                    formData={formData}
                                    setFormData={setFormData}
                                    onPrev={handlePrev}
                                    onNext={handleNext}
                                    secteur={selectedSecteur} // ou handleSubmit si c'est la dernière étape
                                    listSecteurs={secteurs}
                                />
                            )
                        }

                        {
                            step === 8 && (
                                <div>
                                    <p style={{ marginBottom: "10px" }}>
                                        Veuillez lire attentivement les conditions générales d'utilisation
                                    </p>

                                    {/* Conteneur scrollable */}
                                    <div
                                        style={{
                                            maxHeight: "300px",
                                            overflowY: "auto",
                                            padding: "15px",
                                            border: "1px solid #ddd",
                                            borderRadius: "8px",
                                            backgroundColor: "#fafafa"
                                        }}
                                    >
                                        <Cgu />
                                    </div>

                                    <div style={{ marginTop: "20px" }}>
                                        <label style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                            <input
                                                type="checkbox"
                                                checked={cguAccepted}
                                                onChange={(e) => setCguAccepted(e.target.checked)}
                                            />
                                            <span>
                                                J'ai lu et j'accepte les <strong>Conditions Générales d'Utilisation</strong>.
                                            </span>
                                        </label>
                                    </div>

                                    <div className="inscriptionClientModal-actions">
                                        <button
                                            type="button"
                                            onClick={() => setStep(7)}
                                        >
                                            Précédent
                                        </button>

                                        <button
                                            type="button"
                                            disabled={!cguAccepted}
                                            onClick={async () => {
                                                await handleSubmit();
                                                handleNext();
                                            }}
                                        >
                                            Valider
                                        </button>
                                    </div>
                                </div>
                            )
                        }


                    </form></>

                )}

                {inscriptionSuccess && (
                    <div className="success-container">
                        <h2>🎉 Inscription réussie</h2>
                        <p>
                            Bienvenue sur notre plateforme, votre compte Prestataire INTERESEAUX est créé avec succès.
                        </p>


                        <div className="inscriptionClientModal-actions">
                            <button type="button" onClick={() => onClose()}>FERMER</button>
                            <button type="button" onClick={() => onSwitch("connexion")}>ME CONNECTER</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
